"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Loader } from "lucide-react";
gsap.registerPlugin(useGSAP);
function ConsultationForm() {
  const {
    control,
    handleSubmit,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    resetForm
  } = useWvcForm();
  const serviceOptions = ["National SEO", "International SEO", "Local SEO", "Ecommerce SEO", "Technical SEO", "SEO Audits"];
  if (isSubmitted && !submitError) {
    return <div className="text-center py-8">
        <p className="font-serif text-2xl text-primary-foreground mb-6" style={{
        lineHeight: 1.2
      }}>
          {successMessage}
        </p>
        <Button type="button" onClick={resetForm} variant="outline" className="rounded-md border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground px-6 py-2">
          Send another
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-7">
      <FormField control={control} name="full_name" rules={{
      required: "Please enter your name",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      },
      maxLength: {
        value: 50,
        message: "Max 50 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="sr-only">Full Name</FormLabel>
            <FormControl>
              <Input {...field} placeholder="Your name" className="h-12 border-0 border-b border-primary-foreground/30 bg-transparent px-1 text-primary-foreground placeholder:text-primary-foreground/60 focus-visible:border-primary-foreground focus-visible:ring-0 focus-visible:ring-offset-0" />
            </FormControl>
            <FormMessage className="text-primary-foreground/90" />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="sr-only">Email Address</FormLabel>
            <FormControl>
              <Input {...field} type="email" placeholder="Email address" className="h-12 border-0 border-b border-primary-foreground/30 bg-transparent px-1 text-primary-foreground placeholder:text-primary-foreground/60 focus-visible:border-primary-foreground focus-visible:ring-0 focus-visible:ring-offset-0" />
            </FormControl>
            <FormMessage className="text-primary-foreground/90" />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      pattern: {
        value: /^[+()\-\s\d]{7,}$/,
        message: "Please enter a valid phone number"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="sr-only">Phone Number</FormLabel>
            <FormControl>
              <Input {...field} type="tel" placeholder="Phone (optional)" className="h-12 border-0 border-b border-primary-foreground/30 bg-transparent px-1 text-primary-foreground placeholder:text-primary-foreground/60 focus-visible:border-primary-foreground focus-visible:ring-0 focus-visible:ring-offset-0" />
            </FormControl>
            <FormMessage className="text-primary-foreground/90" />
          </FormItem>} />

      <FormField control={control} name="service_type" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-[0.14em] text-primary-foreground/70 font-medium">
              Service Interest
            </FormLabel>
            <FormControl>
              <div className="flex flex-wrap gap-2 pt-2">
                {serviceOptions.map((service, i) => {
            const active = field.value === service;
            return <Button key={service} data-index={i} type="button" onClick={() => field.onChange(service)} variant="outline" className={"rounded-full transition-colors duration-200 " + (active ? "bg-primary-foreground text-primary border-primary-foreground" : "bg-transparent text-primary-foreground border-primary-foreground/40 hover:bg-primary-foreground/10")}>
                      {service}
                    </Button>;
          })}
              </div>
            </FormControl>
            <FormMessage className="text-primary-foreground/90" />
          </FormItem>} />

      <FormField control={control} name="terms" rules={{
      validate: (v: boolean) => v === true || "Please accept the terms"
    }} render={({
      field
    }) => <FormItem>
            <label className="flex items-start gap-3 cursor-pointer">
              <FormControl>
                <input type="checkbox" checked={!!field.value} onChange={e => field.onChange(e.target.checked)} className="mt-1 h-4 w-4 accent-primary-foreground cursor-pointer" />
              </FormControl>
              <span className="text-xs leading-relaxed text-primary-foreground/70">
                I agree to the privacy policy
              </span>
            </label>
            <FormMessage className="text-primary-foreground/90" />
          </FormItem>} />

      {submitError && <p className="text-sm text-primary-foreground">{submitError}</p>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-md bg-primary-foreground text-primary hover:bg-primary-foreground/90 h-12 text-base font-medium">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Sending...
          </> : "Hire Me Directly"}
      </Button>
    </Form>;
}
export default function Consultation() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".soc-headline", {
        y: 24
      });
      gsap.set(".soc-support", {
        y: 16
      });
      gsap.set(".soc-meta", {
        y: 12
      });
      gsap.set(".soc-card", {
        y: 24
      });
      gsap.set(".soc-kicker", {
        y: 12
      });
      const tl = gsap.timeline({
        defaults: {
          ease: "power3.out"
        }
      });
      tl.to(".soc-kicker", {
        opacity: 1,
        y: 0,
        duration: 0.7
      }).to(".soc-headline", {
        opacity: 1,
        y: 0,
        duration: 0.9
      }, "-=0.4").to(".soc-marker", {
        opacity: 1,
        duration: 0.7,
        ease: "power2.out"
      }, "-=0.3").to(".soc-support", {
        opacity: 1,
        y: 0,
        duration: 0.7
      }, "-=0.4").to(".soc-meta", {
        opacity: 1,
        y: 0,
        duration: 0.6
      }, "-=0.4").to(".soc-card", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power2.out"
      }, "-=0.5");
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".soc-kicker", ".soc-headline", ".soc-marker", ".soc-support", ".soc-meta", ".soc-card"], {
        opacity: 1,
        y: 0
      });
    });
  }, {
    scope: container
  });
  return <section data-section-id="30653" id="book-your-free-seo-consultation" className="bg-primary text-primary-foreground">
      <div ref={container} className="mx-auto max-w-[1280px] px-6 md:px-10 py-32 md:py-48">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <p className="soc-kicker text-xs md:text-sm font-medium uppercase tracking-[0.14em] text-primary-foreground/80" style={{
            opacity: 0
          }}>
              Get Started
            </p>

            <h2 className="soc-headline font-serif text-primary-foreground mt-6" style={{
            opacity: 0,
            fontSize: "clamp(2.25rem, 4.5vw, 3.7rem)",
            fontWeight: 400,
            letterSpacing: "-0.03em",
            lineHeight: 0.98
          }}>
              Ready to Discuss
              <span className="relative inline-block ml-3 md:ml-4">
                <span className="italic relative z-10">Your SEO Goals</span>
                <svg aria-hidden="true" className="soc-marker absolute left-0 right-0 -bottom-2 md:-bottom-3 w-full h-4 md:h-6" viewBox="0 0 400 24" preserveAspectRatio="none" style={{
                opacity: 0
              }}>
                  <path d="M4 14 C 60 6, 140 20, 210 12 S 340 6, 396 14" stroke="hsl(340, 75%, 65%)" strokeWidth="10" strokeLinecap="round" fill="none" opacity="0.85" />
                </svg>
              </span>
              <span className="text-primary-foreground">?</span>
            </h2>

            <div className="soc-support mt-8 max-w-md space-y-4" style={{
            opacity: 0
          }}>
              <p className="font-default text-primary-foreground/85" style={{
              fontSize: "1.265rem",
              lineHeight: 1.55
            }}>
                Tell me about your website, current SEO challenges and business goals, and I can assess where focused SEO support could make the greatest difference.
              </p>
              <p className="font-default text-primary-foreground/85" style={{
              fontSize: "1.265rem",
              lineHeight: 1.55
            }}>
                If you're ready to get started, get in touch. This can be the starting point for an SEO project or a focused SEO campaign.
              </p>
            </div>

            <p className="soc-meta mt-6 text-sm text-primary-foreground/70 font-default" style={{
            opacity: 0
          }}>
              Submit your details below and I will review your website within 24 hours.
            </p>
          </div>

          <div className="lg:col-span-5 w-full">
            <div className="soc-card rounded-lg border p-8 md:p-12" style={{
            opacity: 0,
            backgroundColor: "hsl(220, 80%, 35%)",
            borderColor: "hsl(220, 80%, 40%)"
          }}>
              <FormProvider formKey="consultation_form_home" formId="consultation_form_home" sectionName="book-your-free-seo-consultation" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              service_type: "National SEO",
              terms: false
            }} validationRules={{
              full_name: {
                required: true,
                minLength: 2,
                maxLength: 50
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              service_type: {
                required: true
              },
              terms: {
                required: true
              }
            }} submitText="Hire Me Directly" successMessage="Request received. I will be in touch." errorMessage="Submission failed. Please try again.">
                <ConsultationForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}